import { lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionType, ComponentType, useLogEventOnMount } from '@cbhq/client-analytics';

import { createErrorLoggers } from './utils/analytics/errorLogger';
import { checkForCamera } from './utils/checkForCamera';
import type { IdCaptureProps } from './types';

const IdCaptureToken = lazy(() => import('./components/IdCaptureToken'));

const IdCaptureSDK = lazy(() => import('./components/IdCaptureSDK'));

const IdCapture = lazy(() => import('./IdCapture'));

export type IdCaptureWrapperProps = IdCaptureProps & {
  isSDKEnabled?: boolean;
};

function IdCaptureWrapper(props: IdCaptureWrapperProps) {
  const { forceFacematchType, isSDKEnabled, track } = props;
  const [fallbackToSDKCapture, setFallbackToSDKCapture] = useState(false);

  // Can't use useErrorLoggers hook here because it's not within the context yet!
  const errorLoggers = useMemo(() => createErrorLoggers(track), [track]);

  useEffect(() => {
    checkForCamera(track);
  }, [track]);

  // sanitize props to remove functions and other non-serializable values
  // non-serializable values are replaced with `true` to indicate that the value is present
  // null and undefined are included to track when props are explicitly set to these values
  const sanitizedProps = useMemo(() => {
    const filteredProps: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(props)) {
      if (value === undefined || value === null) {
        filteredProps[key] = value;
      } else if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'symbol' ||
        typeof value === 'bigint'
      ) {
        filteredProps[key] = value;
      } else {
        filteredProps[key] = true;
      }
    }

    // sometime referrer is overridden by HTTP referrer header in analytics amplitude, hence using a different key here
    filteredProps.idv_referrer = filteredProps.referrer;

    if (isSDKEnabled) {
      filteredProps.sdk_vendor = props.featureFlags?.isPersonaSDKEnabled
        ? 'SDK_VENDOR_PERSONA'
        : 'SDK_VENDOR_ONFIDO';
    }

    return filteredProps;
  }, [props, isSDKEnabled]);

  // Can't use useTrackOnMount hook here because it's not within the context yet!
  useLogEventOnMount('id_capture', {
    action: ActionType.render,
    componentType: ComponentType.content_script,
    content: 'init',
    ...sanitizedProps,
  });

  const handleNetworkErrorFallbackToSDKCapture = useCallback(() => {
    setFallbackToSDKCapture(true);
  }, []);

  // If a token is provided, use the dedicated token-based flow
  if (props.token) {
    return <IdCaptureToken {...props} token={props.token} />;
  }

  // If SDK is enabled or we've already fallen back to Onfido, use SDK flow
  if (isSDKEnabled || fallbackToSDKCapture) {
    return <IdCaptureSDK {...props} isSDKEnabled fallbackToSDKCapture={fallbackToSDKCapture} />;
  }

  const hasInvalidFacematchType = forceFacematchType && forceFacematchType !== 'face';

  if (hasInvalidFacematchType) {
    // log error if incorrect forceFacematchType is set
    errorLoggers.idCaptureLogAnomaly({
      name: 'invalidProps',
      message: `invalid value is set for props forceFacematchType - ${forceFacematchType}`,
    });
  }

  return (
    <IdCapture
      {...props}
      forceFacematchType={hasInvalidFacematchType ? undefined : forceFacematchType}
      onNetworkErrorFallbackToSDKCapture={handleNetworkErrorFallbackToSDKCapture}
    />
  );
}

export default IdCaptureWrapper;
